import { useState, useCallback, useEffect } from "react";
import { format, isToday, isYesterday } from "date-fns";
import {
  Mail,
  MailOpen,
  Paperclip,
  Star,
  Loader2,
  Sparkles,
  Send,
  ChevronLeft,
  Wand2,
} from "lucide-react";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useEmails, useGmailDraft, useGmailSend, useClassifyEmails, useMarkEmailRead } from "@/hooks/useEmails";
import { useIntegrations } from "@/hooks/useIntegrations";
import { useTextConversations } from "@/hooks/useTextMessages";
import { InboxFilters, type InboxTab } from "@/components/inbox/InboxFilters";
import { TextRow } from "@/components/inbox/TextRow";
import { TextConversationView } from "@/components/inbox/TextConversation";
import { useCommandStyle } from "@/contexts/CommandStyleContext";
import { supabase } from "@/lib/supabase";
import { sanitizeEmailHtml } from "@/lib/sanitize-email";
import type { Email, EmailAttachment, Integration } from "@/lib/types";
import { formatError } from "@/lib/utils";

function formatEmailDate(dateStr: string) {
  const date = new Date(dateStr);
  if (isToday(date)) return format(date, "h:mm a");
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMM d");
}

const categoryColors: Record<string, string> = {
  important: "bg-red-500/10 text-red-500 border-red-500/20",
  actionable: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
  fyi: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  noise: "bg-muted text-muted-foreground border-border",
};

// Compute a stable display label for each Gmail account. When two accounts
// share the same local-part (e.g. skyler@hellaintel.com + skyler@gmail.com),
// the unqualified "skyler" label is ambiguous on the inbox tabs — we append
// the domain prefix so the user can tell them apart. Custom labels set in
// Settings win unconditionally.
function deriveAccountLabels(accounts: Integration[]): Record<string, string> {
  const localPartCounts = new Map<string, number>();
  for (const a of accounts) {
    const cfg = a.config as Record<string, string>;
    if (!cfg.email) continue;
    const custom = cfg.label?.trim();
    if (custom) continue; // explicit labels never collide
    const local = cfg.email.split("@")[0];
    localPartCounts.set(local, (localPartCounts.get(local) || 0) + 1);
  }
  const out: Record<string, string> = {};
  for (const a of accounts) {
    const cfg = a.config as Record<string, string>;
    const email = cfg.email;
    if (!email) continue;
    const custom = cfg.label?.trim();
    if (custom) {
      out[email] = custom;
      continue;
    }
    const [local, domain = ""] = email.split("@");
    if ((localPartCounts.get(local) || 0) > 1) {
      const domainShort = domain.split(".")[0]; // hellaintel.com → hellaintel
      out[email] = `${local} (${domainShort})`;
    } else {
      out[email] = local;
    }
  }
  return out;
}

function AccountTabs({
  accounts,
  accountLabels,
  selected,
  onToggle,
  onClear,
}: {
  accounts: Integration[];
  accountLabels: Record<string, string>;
  selected: string[];
  onToggle: (email: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="flex gap-1 overflow-x-auto pb-1">
      <button
        onClick={onClear}
        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
          selected.length === 0
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground hover:text-foreground"
        }`}
      >
        All Accounts
      </button>
      {accounts.map((acct) => {
        const config = acct.config as Record<string, string>;
        const email = config.email;
        const label = accountLabels[email] || email.split("@")[0];
        const isSelected = selected.includes(email);
        return (
          <button
            key={acct.id}
            onClick={() => onToggle(email)}
            title={email}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              isSelected
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function EmailRow({
  email,
  onClick,
  accountLabels,
}: {
  email: Email;
  onClick: () => void;
  accountLabels?: Record<string, string>;
}) {
  const accountLabel = email.gmail_account
    ? accountLabels?.[email.gmail_account] || email.gmail_account.split("@")[0]
    : null;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-3 border-b border-border hover:bg-muted/50 transition-colors ${
        !email.is_read ? "bg-muted/30" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="pt-0.5 shrink-0">
          {email.is_read ? (
            <MailOpen className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Mail className="h-4 w-4 text-primary" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className={`text-sm truncate ${
                !email.is_read ? "font-semibold" : ""
              }`}
            >
              {email.from_name || email.from_address}
            </span>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {formatEmailDate(email.date)}
            </span>
          </div>
          <p
            className={`text-sm truncate ${
              !email.is_read ? "font-medium" : "text-muted-foreground"
            }`}
          >
            {email.subject || "(no subject)"}
          </p>
          {email.ai_summary ? (
            <p className="text-xs text-muted-foreground truncate mt-0.5 italic">
              {email.ai_summary}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {email.snippet}
            </p>
          )}
          <div className="flex items-center gap-1 mt-1">
            {email.ai_category && (
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${categoryColors[email.ai_category] || ""}`}>
                {email.ai_category}
              </Badge>
            )}
            {email.is_starred && (
              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            )}
            {email.has_attachments && (
              <Paperclip className="h-3 w-3 text-muted-foreground" />
            )}
            {accountLabel && (
              <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-muted">
                {accountLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function EmailDetail({
  email,
  accounts,
  onBack,
}: {
  email: Email;
  accounts: Integration[];
  onBack: () => void;
}) {
  const [replyText, setReplyText] = useState("");
  const [showReply, setShowReply] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [loadingAtt, setLoadingAtt] = useState<string | null>(null);
  const draft = useGmailDraft();
  const send = useGmailSend();
  const markRead = useMarkEmailRead();

  useEffect(() => {
    if (!email.is_read) markRead.mutate(email.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email.id, email.is_read]);

  const attachments: EmailAttachment[] = email.attachments || [];

  // Intercept image clicks inside email HTML to open lightbox
  const handleEmailClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      e.preventDefault();
      const src = (target as HTMLImageElement).src;
      if (src) setLightboxSrc(src);
    }
    if (target.closest("a") && (e.target as HTMLElement).tagName === "IMG") {
      e.preventDefault();
    }
  }, []);

  const handleAttachmentClick = useCallback(async (att: EmailAttachment) => {
    setLoadingAtt(att.attachmentId);
    try {
      const { data, error } = await supabase.functions.invoke("gmail-attachment", {
        body: { email_id: email.id, attachment_id: att.attachmentId },
      });
      if (error) throw error;
      const base64 = data.data as string;
      const dataUri = `data:${att.mimeType};base64,${base64}`;

      if (att.mimeType.startsWith("image/")) {
        setLightboxSrc(dataUri);
      } else {
        // Download non-image files
        const byteChars = atob(base64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: att.mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = att.filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error(`Failed to load attachment: ${formatError(err)}`);
    } finally {
      setLoadingAtt(null);
    }
  }, [email.id]);

  const integration = accounts.find(
    (a) => (a.config as Record<string, string>).email === email.gmail_account
  );

  const handleAiDraft = async () => {
    try {
      const result = await draft.mutateAsync(email.id);
      if (result?.draft) {
        setReplyText(result.draft);
        setShowReply(true);
      }
    } catch (err) {
      toast.error(`AI draft failed: ${formatError(err)}`);
    }
  };

  const handleSend = async () => {
    if (!integration || !replyText.trim()) return;
    try {
      await send.mutateAsync({
        integration_id: integration.id,
        to: email.from_address,
        subject: email.subject?.startsWith("Re:") ? email.subject : `Re: ${email.subject || ""}`,
        body: replyText,
        in_reply_to: email.gmail_message_id,
        thread_id: email.thread_id || undefined,
      });
      toast.success("Reply sent");
      setReplyText("");
      setShowReply(false);
    } catch (err) {
      toast.error(`Send failed: ${formatError(err)}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">
            {email.subject || "(no subject)"}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">
                {email.from_name || email.from_address}
              </p>
              <p className="text-xs text-muted-foreground">
                {email.from_address}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">
                {format(new Date(email.date), "MMM d, yyyy h:mm a")}
              </p>
              <div className="flex items-center gap-1 justify-end mt-0.5">
                <Badge variant="outline" className="text-[10px]">
                  {(() => {
                    const acct = accounts.find(
                      (a) => (a.config as Record<string, string>).email === email.gmail_account
                    );
                    const customLabel = (acct?.config as Record<string, string> | undefined)?.label?.trim();
                    return customLabel || email.gmail_account.split("@")[0];
                  })()}
                </Badge>
                {email.ai_category && (
                  <Badge variant="outline" className={`text-[10px] ${categoryColors[email.ai_category] || ""}`}>
                    {email.ai_category}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            To: {(email.to_addresses || []).join(", ")}
          </p>
          {email.cc_addresses && email.cc_addresses.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Cc: {email.cc_addresses.join(", ")}
            </p>
          )}
        </div>

        <div className="border-t border-border pt-4">
          {email.body_html ? (
            <div
              className="email-body-dark prose prose-sm prose-invert max-w-none text-sm [&_*]:max-w-full [&_img]:max-w-full [&_img]:cursor-pointer"
              dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(email.body_html) }}
              onClick={handleEmailClick}
            />
          ) : (
            <pre className="text-sm whitespace-pre-wrap font-sans">
              {email.body_text || email.snippet || "(no body)"}
            </pre>
          )}
        </div>

        {attachments.length > 0 && (
          <div className="border-t border-border pt-3 mt-2">
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              {attachments.length} attachment{attachments.length > 1 ? "s" : ""}
            </p>
            <div className="flex flex-wrap gap-2">
              {attachments.map((att) => {
                const isImage = att.mimeType.startsWith("image/");
                const sizeLabel = att.size < 1024
                  ? `${att.size} B`
                  : att.size < 1048576
                  ? `${Math.round(att.size / 1024)} KB`
                  : `${(att.size / 1048576).toFixed(1)} MB`;
                const isLoading = loadingAtt === att.attachmentId;

                return (
                  <button
                    key={att.attachmentId}
                    onClick={() => handleAttachmentClick(att)}
                    disabled={isLoading}
                    className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs hover:bg-muted transition-colors max-w-[200px]"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    ) : isImage ? (
                      <div className="h-8 w-8 rounded bg-muted flex items-center justify-center shrink-0 text-[10px] font-bold text-muted-foreground">
                        IMG
                      </div>
                    ) : (
                      <div className="h-8 w-8 rounded bg-muted flex items-center justify-center shrink-0 text-[10px] font-bold text-muted-foreground">
                        {att.filename.split(".").pop()?.toUpperCase().slice(0, 3) || "FILE"}
                      </div>
                    )}
                    <div className="min-w-0 text-left">
                      <p className="truncate font-medium">{att.filename}</p>
                      <p className="text-muted-foreground">{sizeLabel}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-border px-4 py-3 space-y-2">
        {!showReply ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => setShowReply(true)}
              disabled={!integration}
            >
              Reply
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAiDraft}
              disabled={draft.isPending || !integration}
            >
              {draft.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Sparkles className="h-3 w-3 mr-1" />
              )}
              AI Draft
            </Button>
          </div>
        ) : (
          <>
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your reply..."
              rows={4}
              className="text-sm"
            />
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSend}
                  disabled={send.isPending || !replyText.trim()}
                >
                  {send.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Send className="h-3 w-3 mr-1" />
                  )}
                  Send
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowReply(false);
                    setReplyText("");
                  }}
                >
                  Cancel
                </Button>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAiDraft}
                disabled={draft.isPending}
              >
                {draft.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Sparkles className="h-3 w-3 mr-1" />
                )}
                AI Draft
              </Button>
            </div>
          </>
        )}
      </div>
      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </div>
  );
}

export default function Messages() {
  const { copy } = useCommandStyle();
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<InboxTab>("all");
  const [cutBs, setCutBs] = useState(false);
  const [selectedChat, setSelectedChat] = useState<{
    chat_id: string;
    chat_type: "individual" | "group";
    chat_title: string | null;
    participants: { phone: string; name?: string | null }[] | null;
    phone_number: string;
    contact_name: string | null;
  } | null>(null);

  const { data: integrations } = useIntegrations();
  const { data: emails, isLoading: emailsLoading } = useEmails(
    selectedAccounts.length > 0 ? selectedAccounts : undefined,
    cutBs
  );

  const toggleAccount = (email: string) => {
    setSelectedAccounts((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };
  const { data: textConversations, isLoading: textsLoading } = useTextConversations();
  const classify = useClassifyEmails();

  const gmailAccounts =
    integrations?.filter((i) => i.type === "gmail" && i.enabled) || [];

  // Collision-aware label derivation — see deriveAccountLabels above.
  const accountLabels = deriveAccountLabels(gmailAccounts);

  const selectedEmail = emails?.find((e) => e.id === selectedEmailId);

  // Count BS emails (noise + fyi) for the badge
  const bsCount = emails?.filter((e) => e.ai_category && e.ai_category !== "important").length ?? 0;

  const handleClassify = async () => {
    try {
      // Force reclassify all emails with the latest prompt
      const result = await classify.mutateAsync(true);
      toast.success(`Classified ${result.classified} emails`);
      // Auto-enable filter after classifying
      setCutBs(true);
    } catch (err) {
      toast.error(`Classification failed: ${formatError(err)}`);
    }
  };

  // Text conversation detail view — minimal layout (no TopBar/CaptureBar)
  if (selectedChat) {
    return (
      <AppLayout minimal>
        <div className="h-[calc(100vh-env(safe-area-inset-top)-4rem)] sm:h-[calc(100vh-4rem)]">
          <TextConversationView
            chatId={selectedChat.chat_id}
            chatType={selectedChat.chat_type}
            chatTitle={selectedChat.chat_title}
            participants={selectedChat.participants}
            phoneNumber={selectedChat.phone_number}
            contactName={selectedChat.contact_name}
            onBack={() => setSelectedChat(null)}
          />
        </div>
      </AppLayout>
    );
  }

  // Email detail view — minimal layout (no TopBar/CaptureBar)
  if (selectedEmail) {
    return (
      <AppLayout minimal>
        <div className="h-[calc(100vh-env(safe-area-inset-top)-4rem)] sm:h-[calc(100vh-4rem)]">
          <EmailDetail
            email={selectedEmail}
            accounts={gmailAccounts}
            onBack={() => setSelectedEmailId(null)}
          />
        </div>
      </AppLayout>
    );
  }

  // Filter emails for starred tab
  const filteredEmails = activeTab === "starred"
    ? emails?.filter((e) => e.is_starred)
    : emails;

  const showEmails = activeTab === "all" || activeTab === "email" || activeTab === "starred";
  const showTexts = activeTab === "all" || activeTab === "texts";
  const isLoading = emailsLoading || textsLoading;

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">{copy.inbox}</h2>
        <Button
          size="sm"
          variant="outline"
          onClick={handleClassify}
          disabled={classify.isPending}
          title="AI classify emails"
        >
          {classify.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Wand2 className="h-3 w-3" />
          )}
        </Button>
      </div>

      <div className="mb-3 space-y-2">
        <InboxFilters
          activeTab={activeTab}
          onTabChange={setActiveTab}
          cutBs={cutBs}
          onCutBsChange={setCutBs}
          hiddenCount={bsCount}
        />
        {gmailAccounts.length > 1 && showEmails && (
          <AccountTabs
            accounts={gmailAccounts}
            accountLabels={accountLabels}
            selected={selectedAccounts}
            onToggle={toggleAccount}
            onClear={() => setSelectedAccounts([])}
          />
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="-mx-4 border-t border-border">
          {activeTab === "all" ? (
            // Interleave texts and emails chronologically
            (() => {
              type InboxItem =
                | { type: "text"; date: string; data: typeof textConversations extends (infer T)[] | undefined ? T : never }
                | { type: "email"; date: string; data: Email };

              const items: InboxItem[] = [];

              if (textConversations) {
                for (const conv of textConversations) {
                  items.push({ type: "text", date: conv.latest_date, data: conv });
                }
              }
              if (filteredEmails) {
                for (const email of filteredEmails) {
                  items.push({ type: "email", date: email.date, data: email });
                }
              }

              items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

              if (items.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <Mail className="h-10 w-10 text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {gmailAccounts.length === 0
                        ? "Connect a Gmail account in Settings to get started."
                        : "No messages yet. Emails sync automatically every few minutes."}
                    </p>
                    {gmailAccounts.length === 0 && (
                      <Button
                        variant="outline"
                        className="mt-3"
                        onClick={() => (window.location.href = "/settings")}
                      >
                        Go to Settings
                      </Button>
                    )}
                  </div>
                );
              }

              return items.map((item) =>
                item.type === "text" ? (
                  <TextRow
                    key={"text-" + item.data.chat_id}
                    conversation={item.data}
                    onClick={() =>
                      setSelectedChat({
                        chat_id: item.data.chat_id,
                        chat_type: item.data.chat_type,
                        chat_title: item.data.chat_title,
                        participants: item.data.participants,
                        phone_number: item.data.phone_number,
                        contact_name: item.data.contact_name,
                      })
                    }
                  />
                ) : (
                  <EmailRow
                    key={"email-" + item.data.id}
                    email={item.data}
                    accountLabels={accountLabels}
                    onClick={() => setSelectedEmailId(item.data.id)}
                  />
                )
              );
            })()
          ) : (
            <>
              {/* Text conversations (texts tab) */}
              {showTexts && textConversations && textConversations.length > 0 && (
                textConversations.map((conv) => (
                  <TextRow
                    key={conv.chat_id}
                    conversation={conv}
                    onClick={() =>
                      setSelectedChat({
                        chat_id: conv.chat_id,
                        chat_type: conv.chat_type,
                        chat_title: conv.chat_title,
                        participants: conv.participants,
                        phone_number: conv.phone_number,
                        contact_name: conv.contact_name,
                      })
                    }
                  />
                ))
              )}

              {/* Emails (email/starred tabs) */}
              {showEmails && filteredEmails && filteredEmails.length > 0 && (
                filteredEmails.map((email) => (
                  <EmailRow
                    key={email.id}
                    email={email}
                    accountLabels={accountLabels}
                    onClick={() => setSelectedEmailId(email.id)}
                  />
                ))
              )}

              {/* Empty states for non-All tabs */}
              {showEmails && (!filteredEmails || filteredEmails.length === 0) &&
                showTexts && (!textConversations || textConversations.length === 0) && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Mail className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {gmailAccounts.length === 0
                      ? "Connect a Gmail account in Settings to get started."
                      : "No messages yet. Emails sync automatically every few minutes."}
                  </p>
                  {gmailAccounts.length === 0 && (
                    <Button
                      variant="outline"
                      className="mt-3"
                      onClick={() => (window.location.href = "/settings")}
                    >
                      Go to Settings
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </AppLayout>
  );
}
